<template>
  <div class="page page-lg page-lg-results">
    <site-header />
    <v-main v-if="(!isLoading && !isProcessing)">
      <div class="app-content site-width py-4">
        <div class="actions">
          <v-btn
            outlined
            rounded
            color="primary"
            style="background: white;"
            data-test="edit_btn"
            @click="onEditQuoteClick"
          >
            <v-icon
              small
              class="mr-1"
            >
              mdi-pencil
            </v-icon>
            <span class="caption font-weight-medium">Edit Quote</span>
          </v-btn>
        </div>

        <lg-results-msg
          :quote-ref="quoteRef"
          :product-title="formMetadata.productTitle"
          :status="status"
          :first-name="journey.firstName"
          :date-of-insurance-start="journey.dateOfInsuranceStart"
          :results-qty="results.length"
          class="mt-4"
          @resubmit="getQuoteResultsLg"
        />

        <div
          v-if="status === 'success'"
          class="mt-4"
        >
          <!-- STICKY HEADER -->
          <v-card
            v-if="$vuetify.breakpoint.mdAndUp"
            v-sticky
            sticky-z-index="6"
            dark
            :sticky-offset="`{top: ${fixedTopIndent}}`"
            color="navy-medium"
            class="text-center mb-4"
          >
            <v-row class="ma-0">
              <v-col
                v-for="name of constants.RESULTS_TABLE_COLUMNS"
                :key="name"
                class="d-flex flex-column justify-center table-right-border table-right-border-dark"
                :class="resultsTableStyles(name)"
              >
                <h3 class="body-2">
                  {{ name }}
                </h3>
              </v-col>
            </v-row>
          </v-card>

          <lg-quote
            v-for="(result, index) in results"
            :key="result.site_id"
            class="quote-container"
            :class="{ 'mt-3': index > 0 }"
            :quote="result"
          />
        </div>

        <adverts-list
          v-if="results.length === 0 && adverts.length > 0"
          :ads-list="adverts"
          :adverts-type="advertsType"
          class="mt-4"
        />

        <lg-related-products
          v-if="relatedSystems.length > 0"
          :related-products="relatedSystems"
          class="mt-4"
        />
      </div>

      <!-- FOOTER -->
      <site-footer
        v-if="config.partnerFooter && !isLoading"
        role="footer"
      >
        <div v-html="config.partnerFooterText || ''" />
      </site-footer>

      <branding-logo
        v-if="config.brandingLogo"
        :branding-type="config.brandingType"
      />
    </v-main>

    <!-- LOADING OVERLAY -->
    <page-loader :value="isLoading || isProcessing" />
  </div>
</template>

<script>
import {
  ref,
  provide,
} from '@vue/composition-api';

import useQuotezoneCommon from '@/composables/useQuotezoneCommon';
import useLeadgenForm from '@/composables/useLeadgenForm';
import LgResultsMsg from './components/LgResultsMsg';
import useProcessing from '@/composables/useProcessing';
import LgQuote from './components/LgQuote';
import LgRelatedProducts from './components/LgRelatedProducts';
import AdvertsList from './components/AdvertsList';

import useInternalTrackers from '@/composables/useInternalTrackers';

import {
  createAbtastyScript,
  removeAbtastyScript,
} from '@/helpers/common';

export default {
  name: 'page-insurance-results',
  components: {
    LgResultsMsg,
    LgQuote,
    LgRelatedProducts,
    AdvertsList,
  },
  inject: [
    'config',
    'formMetadata',
    'productVersion',
    'constants',
  ],
  data () {
    return {
      quoteRef: null,
      results: [],
      relatedSystems: [],
      adverts: [],
      advertsType: '',
      journey: null,
      statusResubmit: null,
    };
  },
  computed: {
    status () {
      if (this.statusResubmit === true) return 'resubmit';
      if (this.statusResubmit === false) return 'resubmitted';
      if (this.results.length === 0) return 'empty';
      return 'success';
    },
  },
  async created () {
    this.toggleProcessing(true);

    const journey = await this.$api.showJourney({
      productName: this.productName,
      productPartnerId: this.config.partner,
      productVersion: this.productVersion,
    });

    if (journey.status === 'success') {
      this.journey = journey.data;
    }

    if (
      this.journey?.returningUser === true &&
        this.journey?.quoteEngineQuote === false
    ) {
      this.$router.push({
        name: `${this.productName}-insurance`,
      }).catch(() => {});

      this.toggleProcessing(false);

      return;
    }

    if (this.config.showRelatedSystems) {
      const relatedSystemsResponse = await this.$api.getRelatedSystems({
        productName: this.productName,
        partner: this.config.partner,
        currentUrl: `https://${this.productName}-insurance.quotezone.co.uk/${this.productName}/index.php`,
      });

      if (relatedSystemsResponse.status === 'success') {
        const { data } = relatedSystemsResponse;
        Array.isArray(data) && (this.relatedSystems = data);
      }
    }

    await this.getQuoteResultsLg();

    if (this.config.showOvertureAdds && this.results.length === 0) {
      const advertsResponse = await this.$api.getOvertureAds(this.formMetadata.productName);

      if (advertsResponse.status === 'success') {
        const { type, adverts } = advertsResponse.data;
        this.advertsType = type;
        Array.isArray(adverts) && (this.adverts = adverts);
      }
    }

    await this.gtmTracking('results');

    createAbtastyScript(this.config);

    this.toggleProcessing(false);
  },
  beforeDestroy () {
    removeAbtastyScript(this.config);
  },
  methods: {
    onEditQuoteClick () {
      this.$router.push({ name: `${this.productName}-insurance` });

      this.internalTrackerResultsPage('editQuote', true);
    },

    async  getQuoteResultsLg () {
      try {
        const { status, data } = await this.$api.getQuoteResultsLg(
          {
            sid: this.$route.params.sid,
            productName: this.productName,
          },
        );

        this.quoteRef = data.quoteRef || null;
        this.statusResubmit = data.resubmit;

        if (status === 'success') {
          this.results = data.results;
        }
      } catch (e) {
        console.log(e);
      }
    },
    resultsTableStyles (name) {
      if (name === 'Company Info') return 'company-info-col';
      if (name === 'Opening Hours') return 'hours-col';
    },
  },
  setup (props, context) {
    const isLoading = ref(false);
    const {
      isProcessing,
      toggleProcessing,
    } = useProcessing();

    const internalTrackers = useInternalTrackers(context.root);

    provide('internalTrackers', internalTrackers);

    return {
      ...internalTrackers,
      ...useQuotezoneCommon(null, context),
      ...useLeadgenForm(null, context),
      isLoading,
      isProcessing,
      toggleProcessing,
    };
  },
};
</script>

<style scoped lang="scss">
.company-info-col {
  min-width: 30%;
  text-align: left;
}

.hours-col {
  text-align: left;
}
</style>

<style lang="scss" scoped src="@/assets/scss/page-width.scss"/>
